import { useState, useRef, useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { carlosComplete } from '../lib/api';
import { useTenant } from '../components/TenantContext';

const COUNT_QUERY = gql`
  query CarlosCount {
    reviews_aggregate { aggregate { count } }
  }
`;

type Msg = { role: 'user' | 'assistant'; text: string };

const SUGGESTIONS = [
  'Was sind die Top-3-Beschwerden der letzten 30 Tage?',
  'Welche Produkte performen am besten?',
  'Wie unterscheidet sich die Stimmung in DE vs. IT?',
  'Welche Reviews brauchen dringend Antwort?',
];

export function CarlosPage() {
  const { data } = useQuery(COUNT_QUERY, { fetchPolicy: 'cache-and-network' });
  const total = data?.reviews_aggregate?.aggregate?.count ?? 0;
  const { selected } = useTenant();

  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs, sending]);

  async function send(text: string) {
    if (!text.trim() || sending) return;
    const next: Msg[] = [...msgs, { role: 'user', text }];
    setMsgs(next);
    setInput('');
    setSending(true);
    try {
      const out = await carlosComplete(
        next.map((m) => ({ role: m.role, content: m.text })),
        selected,
      );
      setMsgs((m) => [...m, { role: 'assistant', text: out.reply }]);
    } catch (e) {
      setMsgs((m) => [
        ...m,
        {
          role: 'assistant',
          text: `Carlos ist gerade nicht erreichbar (${(e as Error).message}). Bitte versuche es in einem Moment erneut.`,
        },
      ]);
    } finally {
      setSending(false);
    }
  }

  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  }

  if (msgs.length === 0) {
    return (
      <div className="page" style={{ padding: 0, display: 'flex' }}>
        <div className="carlos-empty">
          <div className="carlos-hero">
            <h1>Frag Carlos zu deinen Reviews.</h1>
            <p>
              Carlos liest alle Reviews aus tamaris.com und hilft dir beim Auswerten, Antworten und
              Zusammenfassen. {total > 0 && <>Aktuell {total.toLocaleString('de-DE')} Reviews im Index.</>}
            </p>
          </div>
          <form
            className="carlos-composer"
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
          >
            <button type="button" className="iconbtn" aria-label="Attach">
              <span className="material-symbols-rounded">add</span>
            </button>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Frag Carlos etwas …"
              rows={1}
            />
            <button type="submit" className="send-btn" aria-label="Senden" disabled={!input.trim()}>
              <span className="material-symbols-rounded">arrow_upward</span>
            </button>
          </form>
          <div className="carlos-suggestions">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className="carlos-suggestion"
                onClick={() => send(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <div className="carlos-chat">
        <div className="carlos-msgs" ref={scrollRef}>
          {msgs.map((m, i) => (
            <div key={i} className={`carlos-msg ${m.role}`}>
              <span className="av">{m.role === 'user' ? 'U' : 'C'}</span>
              <div className="bubble">{m.text}</div>
            </div>
          ))}
          {sending && (
            <div className="carlos-msg">
              <span className="av">C</span>
              <div className="bubble" style={{ padding: 0 }}>
                <div className="carlos-typing">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="carlos-foot">
          <form
            className="carlos-composer"
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Antworte oder frag weiter …"
              rows={1}
            />
            <button type="submit" className="send-btn" aria-label="Senden" disabled={!input.trim() || sending}>
              <span className="material-symbols-rounded">arrow_upward</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
